import React from 'react';
import { Card } from '../card';
import { useTheme } from '../../theme/ThemeProvider';
import { styledComponent } from '../../hoc';
import { Typography } from '../typography';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';

/**
 * Props for the header component of the `PluginCard`.
 */
export type HeaderComponentProps = {
  /** The title text displayed in the header. */
  title?: string;
  /** The subheader text displayed below the title. */
  subheader?: string;
  /**
   * Image displayed in the header.
   * Includes light and dark mode URLs and size properties.
   */
  image?: {
    light: string; // Image URL for light mode.
    dark: string; // Image URL for dark mode.
    size: {
      width: string; // Width of the image.
      height: string; // Height of the image.
    };
  };
  /** Custom action element displayed on the right side of the header. */
  action?: React.ReactNode;
};

/**
 * Props for the `PluginCard` component.
 */
export type PluginCardProps = HeaderComponentProps & {
  /** Children elements displayed inside the card content area. */
  children?: React.ReactNode;
  /** Boolean for enabling styling for plugin content that only has Mui Data Grid   */
  isContentMuiDataGridOnly?: boolean;
};

/**
 * A `PluginCard` component for displaying content with a customizable header.
 *
 * @param props - Props for the `PluginCard` component.
 * @returns A styled card component.
 *
 * @category Component
 * @document PluginCardComponentUsage.md
 */
export const PluginCard = (props: PluginCardProps) => {
  return (
    <Card variant="default">
      {props.title && <CardHeader title={<HeaderComponent {...props} />} />}
      <CardContent
        sx={
          props.isContentMuiDataGridOnly
            ? {
                '&:last-child': {
                  paddingBottom: '0px',
                },
              }
            : {}
        }
      >
        {props.children}
      </CardContent>
    </Card>
  );
};

/** Styled container for the header component. */
const Container = styledComponent('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}));

/** Styled wrapper for the title section of the header component. */
const TitleWrapper = styledComponent('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

/**
 * A header component for the `PluginCard`, displaying an optional image,
 * title, subheader, and custom action element.
 *
 * @param props - Props for the header component.
 * @returns A styled header component.
 *
 * @inner
 */
const HeaderComponent = (props: HeaderComponentProps) => {
  const { theme } = useTheme();
  return (
    <Container>
      <TitleWrapper>
        {props.image && (
          <img
            src={theme === 'light' ? props.image.light : props.image.dark}
            alt="logo"
            width={props.image.size.width}
            height={props.image.size.height}
            style={{ objectFit: 'contain' }}
          />
        )}
        <div
          style={{
            marginLeft: '12px',
            display: 'inline-flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="titleMD" fontWeight="bold">
            {props.title}
          </Typography>
          <Typography variant="bodySM">{props.subheader}</Typography>
        </div>
      </TitleWrapper>
      <div>{props.action}</div>
    </Container>
  );
};
